import { proxy } from 'valtio'
import { derive, devtools } from 'valtio/utils'
import { type ApiCallType } from '../../../../../../../../shared/types/ApiCallType'
import { resetProxy } from '../../../../../../../../shared/utils/proxyUtils'
import { type GlobalCustomerType } from '../../../../../../../../shared/types/GlobalCustomerType'
import { type GlobalCustomerCardType } from '../../../../../../../../shared/types/GlobalCustomerCardType'
import { UpdateCardRadioValues } from '../../utils/UpdateCardRadioValues'
import { PaymentResolveRadioValues } from '../../utils/PaymentResolveRadioValues'
import { RpOccurrenceEditProxyFormFieldNamesEnum } from './rpOccurrenceEditProxyFormFieldNamesEnum'
import { fieldsRequiredForSubmission } from './validators'

interface FormType {
  values: { [key in RpOccurrenceEditProxyFormFieldNamesEnum]?: string | Date | number | null }
  errors: { [key in RpOccurrenceEditProxyFormFieldNamesEnum]?: string | null }
  dirty: readonly RpOccurrenceEditProxyFormFieldNamesEnum[]
  blurred: readonly RpOccurrenceEditProxyFormFieldNamesEnum[]
}

export interface RpOccurrenceEditProxyType {
  displaySlideOver: boolean
  allowSubmit: boolean
  apiCallEditConfiguration: ApiCallType
  apiCall: ApiCallType
  completed: boolean
  customer: GlobalCustomerType | null
  customerCard: GlobalCustomerCardType | null
  form: FormType
  showNextPaymentDateError: boolean
  resumeRadioValue: string | null
  updateCardRadioValue: string | null
}

const initialValues = {
  achAccount: null,
  apiCall: {
    error: null,
    loading: false
  },
  apiCallEditConfiguration: {
    error: null,
    loading: false
  },
  completed: false,
  customer: null,
  customerCard: null,
  displaySlideOver: false,
  form: {
    blurred: [],
    dirty: [],
    errors: {},
    values: {}
  },
  lastTransactionId: null,
  resumeRadioValue: PaymentResolveRadioValues.DONT_RESUME_PAYMENT,
  updateCardRadioValue: UpdateCardRadioValues.UPDATE_CARD
}

const baseProxy = proxy(initialValues)

const showErrorFunctionGenerator = (fieldName: RpOccurrenceEditProxyFormFieldNamesEnum) => (get: any) => {
  const { form: { dirty, blurred, errors } } = get(baseProxy)
  const isDirty = (dirty as string[]).includes(fieldName)
  const isBlurred = (blurred as string[]).includes(fieldName)
  const isErrored = Boolean((errors as { [key in RpOccurrenceEditProxyFormFieldNamesEnum]?: string | null })[fieldName])
  return isDirty && isBlurred && isErrored
}

export const rpOccurrenceEditProxy = derive({
  allowSubmit: (get) => {
    const { form: { dirty, errors }, resumeRadioValue } = get(baseProxy)
    const noErrors = Object.values(errors).filter(x => x).length === 0
    const requiredFields = fieldsRequiredForSubmission(resumeRadioValue)
    // @ts-expect-error
    const allDirtied = requiredFields.every((x: string) => dirty.includes(x))
    return noErrors && allDirtied
  },

  showNextPaymentDateError: showErrorFunctionGenerator(RpOccurrenceEditProxyFormFieldNamesEnum.NextPaymentDate)

}, { proxy: baseProxy }) as RpOccurrenceEditProxyType

devtools(rpOccurrenceEditProxy, { name: 'rpOccurrenceEditProxy' })

export const clearApiErrors = (): void => {
  rpOccurrenceEditProxy.apiCallEditConfiguration.error = null
}

export const resetRpOccurrenceEditProxy = (): void => {
  resetProxy(initialValues, rpOccurrenceEditProxy)
}
